"use client";

/**
 * AdminHeader — 관리자 상단 헤더
 *
 * - 모바일: 좌측 햄버거 버튼으로 사이드 메뉴 Drawer 를 연다.
 * - 데스크탑: 사이드바 접기/펼치기 토글 버튼을 제공한다.
 * - 우측에는 사용자 지정 액션(actions)과 사용자 메뉴(user)를 배치한다.
 *
 * 비즈니스 로직(로그아웃 처리 등)은 콜백(onLogout)으로 외부에서 주입한다.
 *
 * @example
 * <AdminHeader title="대시보드" user={user} onLogout={signOut} />
 *
 * @example
 * // 헤더 색 커스터마이징 + 프로필 클릭 시 이동
 * <AdminHeader
 *   user={user}
 *   theme={{ background: "#111827", foreground: "#fff" }}
 *   profileHref="/profile"
 *   LinkComponent={Link}
 *   onLogout={signOut}
 * />
 */
import { type CSSProperties, type ElementType, type ReactNode, useState } from "react";
import { ChevronDown, LogOut, Menu, PanelLeft, User } from "lucide-react";

import { cn } from "../../lib/utils";
import type { AdminUser, HeaderTheme, LinkComponent } from "../../types";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Separator } from "../ui/separator";
import { useAdminLayout } from "./admin-layout-context";

/** HeaderTheme 값을 헤더 루트에 적용할 인라인 style 로 변환한다. 지정한 값만 덮어쓴다. */
function headerThemeToStyle(theme?: HeaderTheme): CSSProperties {
  if (!theme) return {};
  const style: CSSProperties = {};
  if (theme.background) style.backgroundColor = theme.background;
  if (theme.foreground) style.color = theme.foreground;
  if (theme.border) style.borderColor = theme.border;
  return style;
}

export interface AdminHeaderProps {
  /** 헤더 좌측 타이틀 (페이지명 등) */
  title?: ReactNode;
  /** 로그인 사용자 정보. 있으면 우측에 사용자 메뉴를 표시한다 */
  user?: AdminUser;
  /** 사용자 메뉴 우측에 추가로 배치할 액션 (알림, 테마 토글 등) */
  actions?: ReactNode;
  /** 로그아웃 클릭 콜백 */
  onLogout?: () => void;
  /** 사용자 드롭다운에 추가할 커스텀 메뉴 항목. 지정하지 않으면 profileHref 로 이동하는 기본 "프로필" 항목을 보여준다 */
  userMenuItems?: ReactNode;
  /** 기본 "프로필" 메뉴 클릭 시 이동할 경로. userMenuItems 를 직접 넘기면 무시된다 */
  profileHref?: string;
  /** 라우팅 링크 컴포넌트 (Next.js: next/link 의 Link, 기본: <a>) */
  LinkComponent?: LinkComponent;
  /** 헤더 색상 테마. 지정하지 않으면 기본 색상(bg-muted/60)을 사용한다 */
  theme?: HeaderTheme;
  className?: string;
}

/** 이름에서 아바타 폴백용 이니셜을 만든다. */
function initials(name: string) {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

export function AdminHeader({
  title,
  user,
  actions,
  onLogout,
  userMenuItems,
  profileHref,
  LinkComponent,
  theme,
  className,
}: AdminHeaderProps) {
  const { isMobile, setMobileOpen, toggleCollapsed } = useAdminLayout();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const Link = (LinkComponent ?? "a") as ElementType;

  return (
    <header
      style={headerThemeToStyle(theme)}
      className={cn(
        "sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b bg-muted/60 px-4",
        className
      )}
    >
      {isMobile ? (
        <Button
          variant="ghost"
          size="icon"
          aria-label="메뉴 열기"
          onClick={() => setMobileOpen(true)}
        >
          <Menu className="size-5" />
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          aria-label="사이드바 토글"
          onClick={toggleCollapsed}
        >
          <PanelLeft className="size-5" />
        </Button>
      )}

      {title != null && (
        <>
          <Separator orientation="vertical" className="mr-1 h-6" />
          <div className="text-base font-semibold">{title}</div>
        </>
      )}

      <div className="ml-auto flex items-center gap-2">
        {actions}
        {user && (
          <DropdownMenu open={userMenuOpen} onOpenChange={setUserMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-9 gap-2 px-1.5 sm:pr-3"
                aria-label="사용자 메뉴"
              >
                <Avatar>
                  {user.avatarUrl && (
                    <AvatarImage src={user.avatarUrl} alt={user.name} />
                  )}
                  <AvatarFallback className="bg-black font-bold text-white">
                    {initials(user.name)}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden text-left text-sm leading-tight sm:block">
                  <span className="block font-medium">{user.name}</span>
                  {user.role && (
                    <span className="block text-xs text-muted-foreground">
                      {user.role}
                    </span>
                  )}
                </span>
                <ChevronDown
                  className={cn(
                    "size-4 shrink-0 text-muted-foreground transition-transform",
                    userMenuOpen && "rotate-180"
                  )}
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-48">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span>{user.name}</span>
                  {user.email && (
                    <span className="text-xs font-normal text-muted-foreground">
                      {user.email}
                    </span>
                  )}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {userMenuItems ??
                (profileHref ? (
                  <DropdownMenuItem asChild>
                    <Link href={profileHref}>
                      <User className="size-4" />
                      프로필
                    </Link>
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem>
                    <User className="size-4" />
                    프로필
                  </DropdownMenuItem>
                ))}
              {onLogout && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem variant="destructive" onSelect={onLogout}>
                    <LogOut className="size-4" />
                    로그아웃
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
}
